<template>
  <div
    v-if="isOpen"
    class="app-modal-container"
  >
    <FocusTrap
      :active="isOpen"
      :escape-deactivates="false"
      :allow-outside-click="false"
      :initial-focus="initialFocus"
      :fallback-focus="() => closeButton as FocusTargetValue"
      :tabbable-options="focusTrapTabbableOptions"
    >
      <!-- The dialog pattern requires the escape key to close the dialog, so the handler belongs on the dialog itself.
           https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/ -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        class="app-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="$slots.default ? undefined : bodyId"
        v-bind="$attrs"
        @keydown.esc="dismiss"
      >
        <div
          :id="titleId"
          class="modal-header"
        >
          <h1 class="modal-title">
            <!-- @slot Title content to place in an <h1> element; should usually consist of text and/or icons -->
            <slot name="title" />
          </h1>
          <!-- @slot Description content -->
          <slot name="description"></slot>
        </div>

        <!-- @slot Allows overriding body and footer elements for the ultimate customizable modal when needed -->
        <slot name="default">
          <div
            :id="bodyId"
            class="modal-body"
          >
            <!-- @slot Body content; should usually consist of text and/or form elements -->
            <slot name="body"></slot>
          </div>
          <div class="modal-footer">
            <!-- @slot Footer content; should usually consist of buttons -->
            <slot name="footer">
              <AppButton
                ref="closeButton"
                :button-style="ButtonStyle.FILLED"
                :size="ButtonSize.LARGE"
                @click="close"
              >
                Okay
              </AppButton>
            </slot>
          </div>
        </slot>
      </div>
    </FocusTrap>
  </div>
</template>

<!-- AppModal: includes setup for accessibility to describe and label the modal dialog by its contents. -->
<script setup lang="ts">
import type { FocusTargetValue, FocusTrapTabbableOptions, Options } from 'focus-trap'
import { FocusTrap } from 'focus-trap-vue'
import { computed, ref } from 'vue'

import AppButton from '@/components/atomic/AppButton/AppButton.vue'
import { ButtonStyle, ButtonSize } from '@/models/Button'

export interface Props {
  id: string
  isOpen: boolean
  initialFocus?: Options['initialFocus']
  isDismissible?: boolean
}
// Required for tests to not fail.
// See more here: https://github.com/focus-trap/focus-trap-react/issues/1002
const focusTrapTabbableOptions: FocusTrapTabbableOptions = {
  displayCheck: import.meta.env.DEV ? 'none' : 'full',
}
const props = withDefaults(defineProps<Props>(), {
  initialFocus: undefined,
  isDismissible: true,
})
const emit = defineEmits<{ 'update:isOpen': [isOpen: boolean] }>()
const titleId = computed(() => {
  return `${props.id}-title`
})
const bodyId = computed(() => {
  return `${props.id}-body`
})
const closeButton = ref<HTMLButtonElement | undefined>()

// The open state is owned by the consumer so that dismissing the dialog cannot desync it.
function close(): void {
  emit('update:isOpen', false)
}

function dismiss(): void {
  if (!props.isDismissible) {
    return
  }
  close()
}

defineOptions({ inheritAttrs: false })
</script>

<style lang="scss" scoped>
.app-modal-container {
  position: absolute;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #80808063;
}

.app-modal {
  display: flex;
  flex: 0 1 500px;
  flex-direction: column;
  max-height: 80%;
  margin: var(--spacing-small);
  background: var(--hearsee-background-bg-main);
  border-radius: var(--corner-radius-l);
  box-shadow: var(--hearsee-elevation-light-4);
}

.modal-header {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  padding: var(--spacing-medium) var(--spacing-medium) var(--spacing-small);

  @extend %font-body-medium;
}

.modal-title {
  @extend %font-headline-small;

  display: flex;
  gap: var(--spacing-small);
  align-items: center;
}

.modal-body {
  flex: 1;
  padding: var(--spacing-small) var(--spacing-medium);
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-regular);
  justify-content: flex-end;
  padding: var(--spacing-small) var(--spacing-medium) var(--spacing-medium);
}
</style>
